import React, {
  useState, useEffect, useCallback,
} from "react"
import * as API from "services/api"
import { categoryShape } from "utils/propTypeShapes"
import { activityHubSubmissionPath, redirectTo } from "utils/routeHelpers"
import ApiResponsePaginationHeaders from "utils/ApiResponsePaginationHeaders"
import Modal, { ModalHeader } from "shared/Modal"
import { PrimaryButton, TertiaryButton } from "shared/buttons"
import { useModal } from "hooks"
import { errorToast } from "shared/toast"
import DeletedSubmission from "./DeletedSubmission"

const rowHrefFunc = (submission) => activityHubSubmissionPath(submission.slug)

const DeletedSubmissionsTab = ({ category }) => {
  const categorySlug = category.slug
  const [submissions, setSubmissions] = useState([])
  const [recoveredSubmission, setRecoveredSubmission] = useState(null)
  const { modalIsOpen, openModal, closeModal } = useModal()
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const fetchDeletedFormSubmissions = useCallback(async (pageToFetch) => {
    setIsLoading(true)
    const response = await API.categoryDeletedFormSubmissions({ categorySlug }, pageToFetch)
    setIsLoading(false)

    if (response.ok) {
      const paginationHeaders = new ApiResponsePaginationHeaders(response)
      setSubmissions((currentSubmissions) => (
        pageToFetch === 1 ? response.data : currentSubmissions.concat(response.data)
      ))
      setPage(pageToFetch)
      setHasMore(pageToFetch < paginationHeaders.totalPages)
    } else {
      errorToast("Something went wrong. Unable to load deleted submissions.")
    }
  }, [categorySlug])

  useEffect(() => {
    fetchDeletedFormSubmissions(1)
  }, [fetchDeletedFormSubmissions])

  const onSubmissionRecovered = (submission) => {
    setSubmissions((currentSubmissions) => (
      currentSubmissions.filter((currentSubmission) => currentSubmission.slug !== submission.slug)
    ))
    setRecoveredSubmission(submission)
    openModal()
  }

  const viewRecoveredSubmission = () => redirectTo(rowHrefFunc(recoveredSubmission))

  const loadMore = () => fetchDeletedFormSubmissions(page + 1)

  return (
    <>
      <div className="tab-title">Deleted Submissions</div>
      <div className="deleted-submissions-tab">
        {
          submissions.length === 0 ? (
            <div>No deleted submissions</div>
          ) : (
            <>
              <div className="deleted-submissions-tab-headers">
                <span>ID</span>
                <span>Submission Date</span>
                <span>Form</span>
                <span>Status</span>
              </div>
              {
                submissions.map((submission) => (
                  <DeletedSubmission
                    key={submission.slug}
                    submission={submission}
                    onSubmissionRecovered={onSubmissionRecovered}
                  />
                ))
              }
            </>
          )
        }
      </div>
      {
        hasMore && !isLoading && (
          <TertiaryButton className="block mx-auto mt-4" onClick={loadMore} text="Load more" />
        )
      }
      <Modal isOpen={modalIsOpen} aria={{ label: "submission recovered" }}>
        <ModalHeader closeModal={closeModal} heading="Submission Recovered" />
        <p className="text-center my-6 w-3/4 mx-auto">
          {`Submission ${recoveredSubmission?.slug} has been recovered and is accessible in the Activity Hub.`}
        </p>
        <div className="flex justify-center mt-8 gap-2">
          <TertiaryButton
            text="Close"
            onClick={closeModal}
          />
          <PrimaryButton
            text="View Submission"
            onClick={viewRecoveredSubmission}
          />
        </div>
      </Modal>
    </>
  )
}

DeletedSubmissionsTab.propTypes = {
  category: categoryShape.isRequired,
}

export default DeletedSubmissionsTab
